import { createApi } from '@reduxjs/toolkit/query/react'
import { BasePageResponse, BaseRequest, BaseResponse } from '../models'
import { Team, User } from '../../models/User'
import { AdditionalObjects, IntegrationEntities } from '../../models/Integration'
import { removeEmptyItems } from '../../common/util'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { IntegrationEmail, IntegrationEmailIdentity, IntegrationEmailThread } from '../../models/Email'
import { EmailFilterPayload } from '../../models/EmailFilter'
import { UserUpdateResponse } from '../../views/UpdateExternalContact/models'
import { SupportedObject } from '../../views/Setup/AIForms/model'
import { FileInfo } from '../media/file'
import { IntegrationEmailAttachment } from '../../models/EmailAttachments'

// Define a service using a base URL and expected endpoints
const _BASE_URL = import.meta.env.REACT_APP_INTEGRATIONS_SERVICE_API_BASE_URL!

interface BaseConnectorRequest extends BaseRequest {
  connector: string
}

export interface RelatedTo {
  id: string
  type?: string | null
}

export interface GetUsersByFilter extends BaseConnectorRequest {
  filters?: {
    name?: string
    type?: string
    externalIds?: string[]
    email?: string
  }
  includeExternalInfos?: boolean
  useExtendedSchema?: boolean
}

export interface UpdateUser extends BaseConnectorRequest {
  relatedTo: RelatedTo
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: any
  contactChangeId?: string
}

export interface CreateContact extends BaseConnectorRequest {
  contact: { [key: string]: unknown }
  firstName?: string
  lastName?: string
  email?: string
  name?: string
  metadata?: Record<string, unknown>
  contactChangeId?: string
}

export interface GetIntegrationEntitiesRequest extends BaseConnectorRequest {
  entityType: string
}

export interface CreateIntegrationEntitiesRequest extends BaseConnectorRequest {
  entityType: string
  relatedTo: RelatedTo
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: { [key: string]: any }
}

export interface UpdateIntegrationEntitiesRequest extends BaseConnectorRequest {
  entityType: string
  entityId: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: { [key: string]: any }
  relatedTo?: RelatedTo
}

export interface GetAdditionalObjectsRequest extends BaseConnectorRequest {
  relatedTo: RelatedTo
  includeWorkflows?: boolean
  includeWorkflowTemplates?: boolean
  meetingStartDate?: string
  meetingEndDate?: string
}

export interface SaveMeetingRequest extends BaseConnectorRequest {
  body: SaveMeetingRequestPayload
}

export interface SaveMeetingRequestPayload {
  roomId: string
  sessionId: string
  description: string
  externalUserIds: string[]
  externalMeetingId?: string | null
  notifyUserId?: string | null
  notifyTeamId?: string | null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  relatedTo: any
  exportType?: string | null
  aiAgentResultId?: string | null
  saveSyncStatus?: boolean
}

export interface SaveNoteRequest extends BaseConnectorRequest {
  relatedTo: RelatedTo
  notifyUserId?: string | null
  notifyTeamId?: string | null
  roomId?: string
  sessionId?: string
  body: SaveNoteRequestPayload
}

export interface SaveNoteRequestPayload {
  note: string
}

export interface SaveResultRequest extends BaseConnectorRequest {
  userId: string
  body: SaveResultRequestPayload
}

export interface SaveResultToCRMRequest extends BaseConnectorRequest {
  body: SaveResultToCRMRequestPayload
}

export interface SaveResultToCRMRequestPayload {
  relatedTo: RelatedTo
  type: 'aiResult' | 'aiAgentResult'
  resultId: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  result?: { [key: string]: any }
  roomId?: string
  sessionId?: string
  notifyUserId?: string | null
  notifyTeamId?: string | null
  fileId?: string
}

export interface SaveAIAgentResultPayload {
  relatedTo: RelatedTo
  resultId: string
  roomId?: string
  sessionId?: string
  fileId?: string
}

export interface SaveAIAgentResultRequest extends BaseConnectorRequest {
  body: SaveAIAgentResultPayload
}

export interface SaveTasksRequest extends BaseConnectorRequest {
  body: SaveTasksRequestPayload
}

export interface SaveTasksRequestPayload {
  relatedTo: RelatedTo
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tasks: { [key: string]: any }[]
  saveSyncStatus?: boolean
}

export enum OperationDirection {
  INBOUND = 'inbound',
  OUTBOUND = 'outbound',
}

export interface IntegrationOperationFilter {
  active?: boolean
  direction?: OperationDirection
  object?: string
  platform?: string
}

export interface RunContactSyncOperationContext {
  max_object_count: number
}

export type RunOperationContext = RunContactSyncOperationContext

export interface RunOperationRequest extends BaseConnectorRequest {
  context: RunOperationContext
  operation: IntegrationOperationFilter
}

export interface SaveResultRequestPayload {
  roomId: string
  sessionId: string
  aiFeatureId: string
  externalUserIds: string[]
  aiResultId?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extra: { [key: string]: any }
}

export interface IntegrationEmailFilterRequest {
  filter?: EmailFilterPayload
  filterId?: string
}

export interface FilterEmailThreadsRequest extends BaseRequest {
  emailConnector: string
  filters: IntegrationEmailFilterRequest
  expectedThreads?: number
  refresh?: boolean
}

export interface GetEmailThreadRequest extends BaseRequest {
  accountId: string
  emailConnector: string
  threadId: string
}

export interface GetEmailRequest extends BaseRequest {
  accountId: string
  emailConnector: string
  emailId: string
}

export interface CreateIntegrationEmailRequestBody {
  inReplyTo?: string
  recipients?: IntegrationEmailIdentity[]
  ccRecipients?: IntegrationEmailIdentity[]
  subject?: string
  body: string
  isDraft?: boolean
}

interface CreateIntegrationEmailRequest extends BaseConnectorRequest {
  body: CreateIntegrationEmailRequestBody
}

export interface GetEmailThreadAttachmentsRequest extends BaseRequest {
  connector: string
  threadId: string
}

export interface LoadEmailAttachmentRequest extends BaseRequest {
  connector: string
  emailId: string
  attachmentId: string
}

interface FilterIntegrationMappingTemplatesRequest {
  active?: boolean
  sourcePlatform?: string
  sourceObject?: string
  destinationPlatform?: string
  destinationObject?: string
}

interface IntegrationMappingTemplatePath {
  sourcePlatform: string
  sourceObject: string
  destinationPlatform: string
  destinationObject: string
  name: string
}

export interface GetDescribeContextDTO {
  objectName: string
}

export interface GetDescribeRequest extends BaseConnectorRequest {
  context: GetDescribeContextDTO
}

export interface GetContactSchemaContextDTO {
  objectName: string
  relatedTo?: RelatedTo
}

export interface GetContactSchemaRequest extends BaseConnectorRequest {
  context: GetContactSchemaContextDTO
}

export interface WealthboxTag {
  name: string
  id: string
  type?: string
  documentType?: string
}

export interface WealthboxTags {
  tags: WealthboxTag[]
}

export const integrationsApi = createApi({
  reducerPath: 'integrationsApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  endpoints: (builder) => ({
    getIntegrationUsers: builder.query<BaseResponse<User[]>, GetUsersByFilter>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/connectors/${request.connector}/get-contacts`,
          method: 'POST',
          body: {
            filters: request.filters,
            includeExternalInfos: request.includeExternalInfos,
            useExtendedSchema: request.useExtendedSchema,
          },
        }
      },
    }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getContactSchema: builder.query<BaseResponse<{ [key: string]: any }>, GetContactSchemaRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/connectors/${request.connector}/get-contact-schema`,
          method: 'POST',
          body: request.context,
        }
      },
    }),
    getIntegrationTeammates: builder.query<BaseResponse<User[]>, GetUsersByFilter>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/connectors/${request.connector}/get-users`,
          method: 'POST',
          body: {
            filters: request.filters ? removeEmptyItems(request.filters) : {},
          },
        }
      },
    }),
    getIntegrationTeams: builder.query<BaseResponse<Team[]>, GetUsersByFilter>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/connectors/${request.connector}/get-teams`,
          method: 'POST',
          body: {
            filters: request.filters ? removeEmptyItems(request.filters) : {},
          },
        }
      },
    }),
    getIntegrationEntities: builder.query<BaseResponse<IntegrationEntities>, GetIntegrationEntitiesRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/connectors/${request.connector}/${request.entityType}`, // TODO: ?
        }
      },
    }),
    createIntegrationEntity: builder.mutation<BaseResponse<IntegrationEntities>, CreateIntegrationEntitiesRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/connectors/${request.connector}/create-entity`,
          method: 'POST',
          body: {
            relatedTo: request.relatedTo,
            sourcePlatform: request.connector,
            sourceObject: request.entityType,
            entityType: request.entityType,
            entity: request.payload,
          },
        }
      },
    }),
    updateIntegrationEntity: builder.mutation<BaseResponse<IntegrationEntities>, UpdateIntegrationEntitiesRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/connectors/${request.connector}/update-entity`,
          method: 'POST',
          body: {
            relatedTo: request.relatedTo,
            sourcePlatform: request.connector,
            sourceObject: request.entityType,
            entityType: request.entityType,
            entityId: request.entityId,
            entity: request.payload,
          },
        }
      },
    }),
    getAdditionalObjects: builder.query<AdditionalObjects, GetAdditionalObjectsRequest>({
      query: (request) => {
        return {
          method: 'POST',
          url: `/accounts/${request.accountId}/connectors/${request.connector}/get-additional-objects`,
          body: {
            relatedTo: request.relatedTo,
            includeWorkflows: request.includeWorkflows,
            includeWorkflowTemplates: request.includeWorkflowTemplates,
            meetingStartDate: request.meetingStartDate,
            meetingEndDate: request.meetingEndDate,
          },
        }
      },
      transformResponse: (response: BaseResponse<AdditionalObjects>) => {
        return response.data
      },
    }),
    updateIntegrationUser: builder.mutation<UserUpdateResponse, UpdateUser>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/connectors/${request.connector}/update-contact`,
          method: 'POST',
          body: {
            relatedTo: removeEmptyItems(request.relatedTo),
            source: request.connector,
            contact: removeEmptyItems(request.payload),
            contactChangeId: request.contactChangeId,
          },
        }
      },
    }),
    createIntegrationContact: builder.mutation<UserUpdateResponse, CreateContact>({
      query: ({ accountId, connector, contact, firstName, lastName, email, name, metadata, contactChangeId }) => ({
        url: `/accounts/${accountId}/connectors/${connector}/create-contact`,
        method: 'POST',
        body: {
          source: connector,
          contact: removeEmptyItems(contact),
          firstName,
          lastName,
          email,
          name,
          metadata,
          contactChangeId,
        },
      }),
    }),
    saveMeeting: builder.mutation<void, SaveMeetingRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/connectors/${request.connector}/save-meeting`,
          body: request.body,
          method: 'POST',
        }
      },
    }),
    saveNote: builder.mutation<void, SaveNoteRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/connectors/${request.connector}/save-note`,
          body: {
            roomId: request.roomId,
            sessionId: request.sessionId,
            relatedTo: request.relatedTo,
            notifyUserId: request.notifyUserId,
            notifyTeamId: request.notifyTeamId,
            note: request.body.note,
          },
          method: 'POST',
        }
      },
    }),
    saveResult: builder.mutation<void, SaveResultRequest>({
      // @deprecated This using the old crm layer should use the other
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/connectors/${request.connector}/users/${request.userId}/create-result`,
          body: request.body,
          method: 'POST',
        }
      },
    }),
    saveResultToCRM: builder.mutation<void, SaveResultToCRMRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/connectors/${request.connector}/save-result`,
          body: request.body,
          method: 'POST',
        }
      },
    }),
    saveAIAgentResult: builder.mutation<void, SaveAIAgentResultRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/connectors/${request.connector}/save-ai-agent-result`,
          body: request.body,
          method: 'POST',
        }
      },
    }),
    saveTasks: builder.mutation<void, SaveTasksRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/connectors/${request.connector}/save-tasks`,
          body: request.body,
          method: 'POST',
        }
      },
    }),
    getEmailThread: builder.query<IntegrationEmailThread, GetEmailThreadRequest>({
      query: (request) => {
        const { accountId, emailConnector, threadId } = request
        return {
          url: `/accounts/${accountId}/email-connectors/${emailConnector}/emails/threads/${threadId}`,
        }
      },
      transformResponse: (response: BaseResponse<IntegrationEmailThread>) => {
        return response.data
      },
    }),
    getEmail: builder.query<IntegrationEmail, GetEmailRequest>({
      query: (request) => {
        const { accountId, emailConnector, emailId } = request
        return {
          url: `/accounts/${accountId}/email-connectors/${emailConnector}/emails/${emailId}`,
          validateStatus: (response) => (response.status >= 200 && response.status <= 299) || response.status === 404,
        }
      },
      transformResponse: (response: BaseResponse<IntegrationEmail>) => {
        return response.data
      },
    }),
    filterEmailThreads: builder.mutation<BasePageResponse<IntegrationEmailThread[]>, FilterEmailThreadsRequest>({
      query: (request) => {
        const { accountId, emailConnector, expectedThreads, refresh, filters } = request
        return {
          method: 'POST',
          url: `/accounts/${accountId}/email-connectors/${emailConnector}/emails/threads/filter`,
          params: {
            expectedThreads,
            refresh,
          },
          body: filters,
        }
      },
    }),
    createIntegrationEmail: builder.mutation<IntegrationEmail, CreateIntegrationEmailRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/email-connectors/${request.connector}/emails`,
          body: request.body,
          method: 'POST',
        }
      },
    }),
    getEmailThreadAttachments: builder.query<IntegrationEmailAttachment[], GetEmailThreadAttachmentsRequest>({
      query: (request) => {
        const { accountId, connector, threadId } = request
        return {
          url: `/accounts/${accountId}/email-connectors/${connector}/emails/threads/${threadId}/attachments`,
          method: 'GET',
        }
      },
      transformResponse: (response: BaseResponse<IntegrationEmailAttachment[]>) => {
        return response.data
      },
    }),
    loadEmailAttachment: builder.mutation<FileInfo, LoadEmailAttachmentRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/email-connectors/${request.connector}/emails/${request.emailId}/attachments/${request.attachmentId}/load`,
          method: 'POST',
        }
      },
      transformResponse: (response: BaseResponse<FileInfo>) => {
        return response.data
      },
    }),
    filterIntegrationMappingTemplates: builder.query<IntegrationMappingTemplatePath[], FilterIntegrationMappingTemplatesRequest>({
      query: (request) => ({
        url: '/integration-mapping-templates',
        method: 'GET',
        params: request,
      }),
      transformResponse: (response: BaseResponse<IntegrationMappingTemplatePath[]>) => response.data,
    }),
    runOperation: builder.mutation<BaseResponse<BaseResponse<Record<string, unknown>>>, RunOperationRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/connectors/${request.connector}/run-integration-operation`,
        method: 'POST',
        body: {
          context: { context: request.context },
          operation: request.operation,
        },
      }),
    }),
    getSupportedObjects: builder.query<SupportedObject[], BaseConnectorRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/connectors/${request.connector}/get-supported-objects`,
        method: 'GET',
      }),
      transformResponse: (
        response: BaseResponse<{
          supported_object: SupportedObject[]
        }>,
      ) => response.data.supported_object,
    }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getDescribe: builder.query<BaseResponse<any>, GetDescribeRequest>({
      query: ({ accountId, connector, context }) => ({
        url: `/accounts/${accountId}/connectors/${connector}/get-describe`,
        method: 'POST',
        body: context,
      }),
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetIntegrationUsersQuery,
  useGetIntegrationEntitiesQuery,
  useLazyGetIntegrationEntitiesQuery,
  useLazyGetIntegrationUsersQuery,
  useUpdateIntegrationUserMutation,
  useCreateIntegrationContactMutation,
  useSaveMeetingMutation,
  useSaveResultMutation,
  useSaveNoteMutation,
  useGetAdditionalObjectsQuery,
  useLazyGetAdditionalObjectsQuery,
  useCreateIntegrationEntityMutation,
  useUpdateIntegrationEntityMutation,
  useLoadEmailAttachmentMutation,
  useLazyGetEmailThreadAttachmentsQuery,
  useGetIntegrationTeammatesQuery,
  useLazyGetIntegrationTeammatesQuery,
  useGetIntegrationTeamsQuery,
  useLazyGetIntegrationTeamsQuery,
  useFilterEmailThreadsMutation,
  useLazyGetEmailThreadQuery,
  useCreateIntegrationEmailMutation,
  useSaveResultToCRMMutation,
  useSaveTasksMutation,
  useLazyGetEmailQuery,
  useFilterIntegrationMappingTemplatesQuery,
  useGetSupportedObjectsQuery,
  useGetDescribeQuery,
  useGetContactSchemaQuery,
  useLazyGetContactSchemaQuery,
  useSaveAIAgentResultMutation,
  useRunOperationMutation,
} = integrationsApi
